/**
 * As listas que vêm do sistema da empresa: escolher uma e ver na hora.
 *
 * - A empresa vê as publicadas e as lê (`GET /v1/listas/{id}/itens`): a lista
 *   é lida do banco do cliente agora, e não fica guardada no CRM. A leitura
 *   de prévia (`POST /v1/listas/{id}/leitura`, que lê também o rascunho) é só
 *   da Cliqfy.
 * - A empresa (gestor, administrador) pede uma lista nova e acompanha o pedido.
 * - A Cliqfy monta a lista a partir de uma operação SQL de listagem publicada,
 *   publica, arquiva e responde os pedidos. Escrever o SQL é das Operações SQL.
 *
 * O desenho segue o protótipo v2: o item do menu com ícone, o convite com uma
 * pílula por coluna, "Lendo do {sistema}…" com o esqueleto da tabela durante a
 * leitura, o tipo de cada coluna no título e a seta da ordem. O passo a passo
 * de montar com SQL, a contagem no item do menu e os tipos dinheiro e data com
 * hora ficaram de fora: pedem contrato ou decisão (§21).
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MONTA_LISTA, PEDE_LISTA } from '@/entities/acesso/papel';
import { chaves } from '@/shared/api/chaves';
import { useApi, useListas, useOperacoesDeDados, usePedidosDeLista } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { usePapel } from '@/shared/sessao/contexto';
import {
  Botao,
  CabecalhoDeTela,
  Campo,
  CampoArea,
  CampoSelecao,
  Dialogo,
  Icone,
  Selo,
  type NomeDoIcone,
} from '@/shared/ui';
import { Carregando, Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import {
  SITUACAO_DA_LISTA,
  filtrarLinhas,
  finalidadeDoNome,
  formatarValor,
  ordenarLinhas,
  proximaOrdem,
  type Coluna,
  type Lista,
  type Ordem,
} from '../lib/listas';
import './listas.css';

/** O ícone do tipo de cada coluna, no título da tabela e na pílula do convite. */
const ICONE_DO_TIPO: Record<Coluna['tipo'], NomeDoIcone> = {
  texto: 'documento',
  numero: 'planilha',
  data: 'calendario',
  booleano: 'check',
};

type Pedido = NonNullable<ReturnType<typeof usePedidosDeLista>['data']>['itens'][number];

/** O que a tela mostra, venha da leitura de prévia ou dos itens. */
type Leitura = {
  colunas: readonly Coluna[];
  linhas: Record<string, unknown>[];
  total: number;
  cortada: boolean;
  lida_em: string;
};

/*
 * Os dois 503 são coisas diferentes: `ENGINE_INDISPONIVEL` é o serviço das
 * automações fora; `BANCO_DO_CLIENTE_INDISPONIVEL`, o sistema da empresa.
 * Nenhum dos dois traz `detail`, então a frase é daqui.
 */
/** A mesma regra do contrato (`FinalidadeDaListaSchema`). */
const FINALIDADE_VALIDA = /^[a-z0-9_]{2,40}$/;

const FORA = {
  ENGINE_INDISPONIVEL:
    'As listas não responderam agora. Nada foi alterado; tente de novo daqui a pouco.',
  BANCO_DO_CLIENTE_INDISPONIVEL:
    'O sistema da empresa não respondeu agora. Nenhum dado foi alterado; tente de novo daqui a pouco.',
};

export function Listas({
  lista: escolhida,
  aoEscolher,
  aoIrParaOperacoes,
}: {
  lista: string | null;
  aoEscolher: (id: string | null) => void;
  /** Abre as Operações SQL, onde se escreve a operação de que a lista lê (só a Cliqfy). */
  aoIrParaOperacoes?: () => void;
}): ReactNode {
  const papel = usePapel();
  const pede = papel !== null && PEDE_LISTA.has(papel);
  const monta = papel !== null && MONTA_LISTA.has(papel);
  const listas = useListas();
  const pedidos = usePedidosDeLista(pede || monta);
  const [pedindo, definirPedindo] = useState(false);
  const [montando, definirMontando] = useState(false);
  const [editando, definirEditando] = useState<Lista | null>(null);
  const [respondendo, definirRespondendo] = useState<Pedido | null>(null);
  const [recado, definirRecado] = useState<string | null>(null);
  /*
   * O que se leu de cada lista nesta visita: quantas linhas vieram, ou que a
   * leitura falhou. Só aparece no menu depois de uma leitura; antes, a tela
   * não sabe o número, e não inventa.
   */
  const [lidas, definirLidas] = useState<Record<string, LidaNoMenu>>({});
  /** As listas publicadas agora, nesta visita: ganham o selo "Publicada agora". */
  const [publicadasAgora, definirPublicadasAgora] = useState<ReadonlySet<string>>(new Set());
  /*
   * Arquivar tira da tela o painel e o botão que abriu a confirmação, e o foco
   * cairia no começo da página. Ele vai para o recado, que diz o que houve; o
   * efeito roda depois que o diálogo fechou e tentou devolver o foco.
   */
  const recadoRef = useRef<HTMLParagraphElement>(null);
  const [focarNoRecado, definirFocarNoRecado] = useState(false);
  useEffect(() => {
    if (!focarNoRecado) return;
    recadoRef.current?.focus();
    definirFocarNoRecado(false);
  }, [focarNoRecado]);

  return (
    <div className="listas">
      <CabecalhoDeTela
        titulo="Listas"
        retrato={
          <span className="listas__icone-do-titulo" aria-hidden="true">
            <Icone nome="planilha" />
          </span>
        }
        apoio="Listas que vêm direto do sistema da sua empresa. Escolha uma e veja na hora."
        {...(pede || monta
          ? {
              acoes: (
                <>
                  {pede && (
                    <Botao variante="primario" onClick={() => definirPedindo(true)}>
                      <Icone nome="criar" />
                      Pedir uma lista nova
                    </Botao>
                  )}
                  {monta && (
                    <Botao variante="primario" onClick={() => definirMontando(true)}>
                      <Icone nome="criar" />
                      Nova listagem
                    </Botao>
                  )}
                </>
              ),
            }
          : {})}
      />
      <div className="listas__conteudo">
        <p
          className="listas__recado"
          role="status"
          aria-live="polite"
          tabIndex={-1}
          ref={recadoRef}
        >
          {recado ?? ''}
        </p>

        <Estado consulta={listas} forma="lista" linhas={4}>
          {(dados) => {
            const publicadas = dados.itens.filter((l) => l.situacao === 'publicada');
            const rascunhos = monta ? dados.itens.filter((l) => l.situacao === 'rascunho') : [];
            const doPedido = (pedidos.data?.itens ?? []).filter((p) =>
              monta ? p.situacao === 'aguardando' : true,
            );
            const atual = dados.itens.find((l) => l.id === escolhida) ?? null;
            const pedidosFalharam = pedidos.isError && (
              <p className="listas__nota" role="status">
                Os pedidos não carregaram agora. Recarregue a página para ver se há pedido
                aguardando.
              </p>
            );
            if (publicadas.length === 0 && rascunhos.length === 0 && doPedido.length === 0) {
              // Sem lista, o vazio só se decide depois dos pedidos: um pedido
              // aguardando é justamente o que quem espera a primeira lista tem.
              if ((pede || monta) && pedidos.isLoading)
                return <Carregando forma="lista" linhas={2} />;
              return (
                <>
                  {pedidosFalharam}
                  <Vazio
                    titulo="Nenhuma lista ainda"
                    porque={
                      monta
                        ? 'Uma lista mostra aqui uma consulta de leitura ao sistema da empresa. Monte a primeira a partir de uma operação de listagem publicada.'
                        : 'Uma lista mostra aqui dados do sistema da sua empresa: a agenda do dia, os orçamentos em aberto. Peça ao suporte Cliqfy a lista que você quer ver.'
                    }
                    {...(pede
                      ? {
                          acao: { rotulo: 'Pedir uma lista', aoClicar: () => definirPedindo(true) },
                        }
                      : monta
                        ? {
                            acao: {
                              rotulo: 'Montar a primeira',
                              aoClicar: () => definirMontando(true),
                            },
                          }
                        : {})}
                  />
                </>
              );
            }
            return (
              <div className="listas__corpo">
                <nav className="listas__menu" aria-label="Listas">
                  <GrupoDeListas
                    titulo={`Publicadas · ${publicadas.length}`}
                    itens={publicadas}
                    lidas={lidas}
                    escolhida={escolhida}
                    aoEscolher={aoEscolher}
                    vazio="Nenhuma lista publicada ainda."
                  />
                  {rascunhos.length > 0 && (
                    <GrupoDeListas
                      titulo="Em montagem · só a Cliqfy vê"
                      itens={rascunhos}
                      lidas={lidas}
                      escolhida={escolhida}
                      aoEscolher={aoEscolher}
                      vazio=""
                    />
                  )}
                  {pedidosFalharam}
                  {doPedido.length > 0 && (
                    <section className="listas__grupo">
                      <h2 className="listas__grupo-titulo">
                        {monta ? 'Pedidos da empresa' : 'Pedidos de listas'}
                      </h2>
                      <ul className="listas__pedidos">
                        {doPedido.map((p) => {
                          // Atendido com uma lista que saiu do ar: ela não vem mais em /v1/listas.
                          const daLista = p.lista_id
                            ? dados.itens.find((l) => l.id === p.lista_id)
                            : undefined;
                          const semLista = p.situacao === 'atendido' && !daLista;
                          return (
                            <li
                              key={p.id}
                              className="listas__pedido"
                              data-situacao={semLista ? 'sem-lista' : p.situacao}
                            >
                              <span className="listas__pedido-icone" aria-hidden="true">
                                <Icone
                                  nome={ICONE_DO_PEDIDO[semLista ? 'sem-lista' : p.situacao]}
                                />
                              </span>
                              <span className="listas__pedido-nome">{p.nome}</span>
                              <span className="listas__pedido-quando">
                                Pedido {idadeEmPalavras(p.criado_em) ?? ''}
                              </span>
                              <span className="listas__pedido-estado">
                                {p.situacao === 'aguardando'
                                  ? 'Aguardando a Cliqfy'
                                  : semLista
                                    ? 'Atendido, mas a lista foi arquivada. Peça de novo se ainda precisar.'
                                    : p.situacao === 'atendido'
                                      ? 'Pronta: já está em Publicadas'
                                      : `Recusado: ${p.motivo ?? 'sem motivo'}`}
                              </span>
                              {monta && p.situacao === 'aguardando' && (
                                <Botao
                                  variante="secundario"
                                  miudo
                                  onClick={() => definirRespondendo(p)}
                                >
                                  Responder
                                </Botao>
                              )}
                              {!monta && daLista && p.situacao === 'atendido' && (
                                <Botao
                                  variante="fantasma"
                                  miudo
                                  onClick={() => aoEscolher(daLista.id)}
                                >
                                  Ver
                                </Botao>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  )}
                </nav>

                <section className="listas__painel" aria-label="A lista escolhida">
                  {atual ? (
                    <PainelDaLista
                      key={atual.id}
                      lista={atual}
                      monta={monta}
                      publicadaAgora={publicadasAgora.has(atual.id)}
                      aoLer={(lida) => definirLidas((antes) => ({ ...antes, [atual.id]: lida }))}
                      aoPublicada={() =>
                        definirPublicadasAgora((antes) => new Set(antes).add(atual.id))
                      }
                      aoMudou={(texto) => definirRecado(texto)}
                      aoArquivada={() => {
                        aoEscolher(null);
                        definirFocarNoRecado(true);
                      }}
                      {...(monta ? { aoEditar: () => definirEditando(atual) } : {})}
                    />
                  ) : (
                    <p className="listas__convite">
                      {publicadas.length > 0
                        ? 'Escolha uma lista à esquerda para ver.'
                        : monta
                          ? 'Nenhuma lista publicada ainda. Monte uma pelo botão "Nova listagem".'
                          : 'Nenhuma lista publicada ainda. Assim que a Cliqfy publicar o seu pedido, ela aparece aqui.'}
                    </p>
                  )}
                </section>
              </div>
            );
          }}
        </Estado>

        {pedindo && (
          <PedirLista
            aoFechar={() => definirPedindo(false)}
            aoPedido={(nome) => {
              definirPedindo(false);
              definirRecado(
                `Pedido ${nome} enviado. A lista aparece aqui quando a Cliqfy publicar.`,
              );
            }}
          />
        )}
        {montando && (
          <MontarLista
            {...(aoIrParaOperacoes ? { aoIrParaOperacoes } : {})}
            finalidades={[...new Set((listas.data?.itens ?? []).map((l) => l.finalidade))]}
            aoFechar={() => definirMontando(false)}
            aoMontada={(id, nome) => {
              definirMontando(false);
              definirRecado(
                `${nome} está em montagem. Leia para conferir e publique quando estiver certa.`,
              );
              aoEscolher(id);
            }}
          />
        )}
        {editando && (
          <MontarLista
            {...(aoIrParaOperacoes ? { aoIrParaOperacoes } : {})}
            lista={editando}
            finalidades={[
              ...new Set(
                (listas.data?.itens ?? [])
                  .filter((l) => l.id !== editando.id)
                  .map((l) => l.finalidade),
              ),
            ]}
            aoFechar={() => definirEditando(null)}
            aoMontada={(_id, nome) => {
              definirEditando(null);
              definirRecado(`${nome} salva. Leia de novo para conferir.`);
            }}
          />
        )}
        {respondendo && (
          <ResponderPedido
            pedido={respondendo}
            publicadas={(listas.data?.itens ?? []).filter((l) => l.situacao === 'publicada')}
            aoFechar={() => definirRespondendo(null)}
            aoRespondido={(texto) => {
              definirRespondendo(null);
              definirRecado(texto);
            }}
          />
        )}
      </div>
    </div>
  );
}

function GrupoDeListas({
  titulo,
  itens,
  escolhida,
  aoEscolher,
  vazio,
  lidas,
}: {
  titulo: string;
  itens: readonly Lista[];
  escolhida: string | null;
  aoEscolher: (id: string) => void;
  vazio: string;
  lidas: Readonly<Record<string, LidaNoMenu>>;
}): ReactNode {
  return (
    <section className="listas__grupo">
      <h2 className="listas__grupo-titulo">{titulo}</h2>
      {itens.length === 0 ? (
        <p className="listas__nota">{vazio}</p>
      ) : (
        <ul className="listas__itens">
          {itens.map((l) => (
            <li key={l.id}>
              <button
                type="button"
                className="listas__item"
                data-situacao={l.situacao}
                aria-current={escolhida === l.id ? 'true' : undefined}
                onClick={() => aoEscolher(l.id)}
              >
                <span className="listas__item-icone" aria-hidden="true">
                  <Icone nome={l.situacao === 'rascunho' ? 'lapis' : 'menu'} />
                </span>
                <span className="listas__item-textos">
                  <span className="listas__item-nome">{l.nome}</span>
                  <span className="listas__item-apoio">
                    {l.colunas.length === 1 ? '1 coluna' : `${l.colunas.length} colunas`}
                    {l.conexao_nome ? ` · ${l.conexao_nome}` : ''}
                  </span>
                </span>
                <LidaDoItem lida={lidas[l.id]} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PainelDaLista({
  lista,
  monta,
  publicadaAgora,
  aoLer,
  aoPublicada,
  aoMudou,
  aoArquivada,
  aoEditar,
}: {
  lista: Lista;
  monta: boolean;
  publicadaAgora: boolean;
  /** Avisa o menu do que a leitura trouxe: quantas linhas, ou que falhou. */
  aoLer: (lida: LidaNoMenu) => void;
  aoPublicada: () => void;
  aoMudou: (texto: string) => void;
  aoArquivada: () => void;
  /** A Cliqfy troca a definição: colunas, nomes e a coluna-chave. */
  aoEditar?: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [busca, definirBusca] = useState('');
  const [ordem, definirOrdem] = useState<Ordem>(null);
  const [confirmandoArquivar, definirConfirmandoArquivar] = useState(false);
  const porQueArquivar = useId();
  // A faixa mostra o erro da última ação, e só dela.
  const [ultima, definirUltima] = useState<'ler' | 'publicar' | null>(null);
  // Lista filtrada pelo cliente só se lê com um cliente escolhido: aqui não há.
  const doCliente = lista.filtro_por_cliente !== null;

  const ler = useMutation({
    mutationFn: async (): Promise<Leitura> => {
      if (monta) {
        const r = await api!.lerLista({ caminho: { id: lista.id }, corpo: {} });
        return { ...r, linhas: r.linhas };
      }
      const r = await api!.itensDaLista({ caminho: { id: lista.id } });
      return {
        colunas: lista.colunas,
        linhas: r.itens.map((i) => i.dados),
        total: r.total,
        cortada: r.cortada,
        lida_em: r.lida_em,
      };
    },
    onSuccess: (lida) => aoLer({ total: lida.total, cortada: lida.cortada }),
    onError: () => aoLer('falhou'),
  });
  const publicar = useMutation({
    mutationFn: () => api!.publicarLista({ caminho: { id: lista.id } }),
    onSuccess: () => {
      aoPublicada();
      aoMudou(`${lista.nome} publicada. Já aparece para a empresa em Listas.`);
    },
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.listas() }),
  });
  const arquivar = useMutation({
    mutationFn: () => api!.arquivarLista({ caminho: { id: lista.id } }),
    onSuccess: () => {
      // Sai do menu já, sem esperar a lista voltar do servidor.
      cache.setQueryData<{ itens: Lista[] }>(chaves.listas(), (atual) =>
        atual ? { ...atual, itens: atual.itens.filter((l) => l.id !== lista.id) } : atual,
      );
      definirConfirmandoArquivar(false);
      aoMudou(`${lista.nome} arquivada. Ela sai da tela e das leituras.`);
      aoArquivada();
    },
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.listas() }),
  });

  const r = ler.data;
  const colunas = r?.colunas ?? lista.colunas;
  // As linhas entram em ordem só na primeira leitura: atualizar não refaz a cena.
  const [leituras, definirLeituras] = useState(0);
  useEffect(() => {
    if (ler.isSuccess) definirLeituras((n) => n + 1);
  }, [ler.data, ler.isSuccess]);
  const linhas = r ? ordenarLinhas(filtrarLinhas(r.linhas, colunas, busca), ordem, colunas) : [];
  /*
   * Só o erro da última ação: um "Publicar" que falhou não fica na tela depois
   * de uma leitura que deu certo. Sem `reset()`: ele volta a mutação ao começo
   * e apagaria junto a tabela lida, ou o andamento de uma ação no meio.
   */
  const fazer = (qual: 'ler' | 'publicar'): void => {
    definirUltima(qual);
    (qual === 'ler' ? ler : publicar).mutate();
  };
  const falha = ultima === 'ler' ? ler.error : ultima === 'publicar' ? publicar.error : null;

  return (
    <div className="listas__lista">
      <div className="listas__topo">
        <div>
          <h2 className="listas__nome">
            {lista.nome}
            {publicadaAgora && (
              <span className="listas__publicada-agora">
                <Icone nome="check" />
                Publicada agora
              </span>
            )}
          </h2>
          {lista.descricao && <p className="listas__desc">{lista.descricao}</p>}
          <p className="listas__nota listas__origem">
            <Icone nome="caixa" />
            {lista.conexao_nome ? `Lê de ${lista.conexao_nome}` : 'Lê do sistema da empresa'}
            {r
              ? ` · lida às ${new Date(r.lida_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
              : ''}
            {monta ? ` · versão ${lista.versao}` : ''}
          </p>
        </div>
        <div className="listas__acoes">
          {monta && (
            <Selo tom={lista.situacao === 'publicada' ? 'ok' : 'rascunho'}>
              {SITUACAO_DA_LISTA[lista.situacao]}
            </Selo>
          )}
          {monta && lista.situacao === 'rascunho' && (
            <Botao
              variante="primario"
              miudo
              onClick={() => fazer('publicar')}
              desabilitado={publicar.isPending}
              motivo="Publicando…"
            >
              Publicar
            </Botao>
          )}
          {monta && aoEditar && (
            <Botao variante="fantasma" miudo onClick={aoEditar}>
              Editar
            </Botao>
          )}
          {monta && (
            <Botao
              variante="fantasma"
              miudo
              onClick={() => {
                // Um erro de uma tentativa anterior não abre junto.
                arquivar.reset();
                definirConfirmandoArquivar(true);
              }}
            >
              Arquivar
            </Botao>
          )}
          {!doCliente && (
            <Botao
              variante={r ? 'secundario' : 'primario'}
              onClick={() => fazer('ler')}
              desabilitado={ler.isPending || api === null}
              motivo={ler.isPending ? 'Lendo…' : 'Sem sessão: entre de novo.'}
            >
              {r && <Icone nome="repetir" />}
              {ler.isPending ? 'Lendo…' : r ? 'Atualizar' : 'Ver lista'}
            </Botao>
          )}
        </div>
      </div>

      {falha && (
        <Faixa
          tom="bloqueado"
          {...(ultima === 'ler'
            ? { acao: { rotulo: 'Tentar de novo', aoClicar: () => fazer('ler') } }
            : {})}
        >
          {recadoDeErro(falha, {
            generica: 'Não deu agora. Nada foi alterado; tente de novo.',
            porCodigo: FORA,
          })}
        </Faixa>
      )}

      {ler.isPending && (
        <div className="listas__lendo" role="status">
          <p className="listas__lendo-aviso">
            <span className="listas__girador" aria-hidden="true" />
            Lendo {lista.conexao_nome ? `do ${lista.conexao_nome}` : 'do sistema da empresa'}…
          </p>
          <div className="listas__rolagem" aria-hidden="true">
            <table className="listas__tabela">
              <thead>
                <tr>
                  {colunas.map((c) => (
                    <th key={c.nome} scope="col">
                      <span className="listas__titulo-da-coluna">{c.titulo}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    {colunas.map((c) => (
                      <td key={c.nome}>
                        <span
                          className="listas__osso"
                          style={
                            {
                              '--largura': `${45 + ((i * 7 + c.nome.length * 11) % 45)}%`,
                            } as CSSProperties
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!r && !ler.isPending && (
        <div className="listas__convite">
          <p>
            Esta lista traz {colunas.length === 1 ? '1 coluna' : `${colunas.length} colunas`}.{' '}
            {doCliente
              ? 'Ela é filtrada pelo cliente e só pode ser lida com um cliente escolhido, por isso não abre aqui.'
              : 'Ela é lida na hora, quando você pedir.'}
          </p>
          <ul className="listas__pilulas" aria-label="Colunas da lista">
            {colunas.map((c, i) => (
              <li
                key={c.nome}
                className="listas__pilula"
                style={{ '--i': Math.min(i, 8) } as CSSProperties}
              >
                <Icone nome={ICONE_DO_TIPO[c.tipo]} />
                {c.titulo}
              </li>
            ))}
          </ul>
        </div>
      )}

      {r &&
        (r.linhas.length === 0 ? (
          <Vazio
            titulo="A lista rodou e não trouxe nenhuma linha"
            porque="O sistema da empresa respondeu normalmente, mas não há nada que se encaixe agora. Não é erro: tente de novo mais tarde."
          />
        ) : (
          <>
            <div className="listas__ferramentas">
              <div className="listas__busca">
                <Campo
                  type="search"
                  icone="buscar"
                  rotuloOculto="Buscar nesta lista"
                  placeholder="Buscar nesta lista"
                  value={busca}
                  onChange={(e) => definirBusca(e.target.value)}
                />
              </div>
              <p className="listas__nota">
                {r.linhas.length === 1
                  ? '1 linha'
                  : `${r.linhas.length.toLocaleString('pt-BR')} linhas`}
                {r.cortada
                  ? ` de ${r.total.toLocaleString('pt-BR')}, cortadas no limite da consulta`
                  : ''}{' '}
                · clique num título para ordenar
              </p>
            </div>
            {linhas.length === 0 ? (
              <p className="listas__nada">
                Nenhuma linha com “{busca.trim()}”.{' '}
                <Botao variante="fantasma" miudo onClick={() => definirBusca('')}>
                  Limpar a busca
                </Botao>
              </p>
            ) : (
              <div className="listas__rolagem">
                <table className="listas__tabela" data-entrando={leituras <= 1 ? 'sim' : undefined}>
                  <thead>
                    <tr>
                      {colunas.map((c) => (
                        <th
                          key={c.nome}
                          scope="col"
                          aria-sort={
                            ordem?.coluna === c.nome
                              ? ordem.direcao === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : undefined
                          }
                          data-numero={c.tipo === 'numero' ? 'sim' : undefined}
                        >
                          <button
                            type="button"
                            className="listas__ordenar"
                            data-ordem={ordem?.coluna === c.nome ? ordem.direcao : undefined}
                            onClick={() => definirOrdem((o) => proximaOrdem(o, c.nome))}
                          >
                            <Icone nome={ICONE_DO_TIPO[c.tipo]} />
                            {c.titulo}
                            <span className="listas__seta" aria-hidden="true">
                              <Icone nome="abaixo" />
                            </span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => (
                      <tr key={i} style={{ '--i': Math.min(i, 8) } as CSSProperties}>
                        {colunas.map((c) => (
                          <td key={c.nome} data-numero={c.tipo === 'numero' ? 'sim' : undefined}>
                            {formatarValor(l[c.nome], c.tipo)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="listas__nota">Só as colunas que a Cliqfy declarou aparecem aqui.</p>
          </>
        ))}

      {confirmandoArquivar && (
        <Dialogo
          titulo={`Arquivar ${lista.nome}?`}
          largura="estreita"
          papel="alertdialog"
          descricaoId={porQueArquivar}
          consequencia
          aoFechar={() => {
            if (!arquivar.isPending) definirConfirmandoArquivar(false);
          }}
          botoes={
            <>
              <Botao
                variante="fantasma"
                onClick={() => definirConfirmandoArquivar(false)}
                desabilitado={arquivar.isPending}
                motivo="Arquivando…"
              >
                Cancelar
              </Botao>
              <Botao
                variante="perigo"
                onClick={() => arquivar.mutate()}
                desabilitado={arquivar.isPending || api === null}
                motivo={arquivar.isPending ? 'Arquivando…' : 'Sem sessão: entre de novo.'}
              >
                {arquivar.isPending ? 'Arquivando…' : 'Arquivar'}
              </Botao>
            </>
          }
        >
          <p className="listas__nota" id={porQueArquivar}>
            A lista sai de Listas e não pode mais ser lida. A operação de que ela lê continua como
            está. Não há como desarquivar: para voltar, ela é montada de novo.
          </p>
          {arquivar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(arquivar.error, {
                generica: 'Não foi possível arquivar agora. Nada foi alterado; tente de novo.',
                porCodigo: FORA,
              })}
            </Faixa>
          )}
        </Dialogo>
      )}
    </div>
  );
}

function PedirLista({
  aoFechar,
  aoPedido,
}: {
  aoFechar: () => void;
  aoPedido: (nome: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [chave] = useState(() => crypto.randomUUID());
  const [nome, definirNome] = useState('');
  const [oQue, definirOQue] = useState('');
  const [colunas, definirColunas] = useState('');
  const [tentou, definirTentou] = useState(false);

  const pedir = useMutation({
    mutationFn: () =>
      api!.pedirLista({
        corpo: {
          nome: nome.trim(),
          o_que: oQue.trim(),
          colunas_sugeridas: colunas.trim() || null,
        },
        idempotencyKey: chave,
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.listas() });
      aoPedido(nome.trim());
    },
  });

  const erroNome = tentou && !nome.trim() ? 'Dê um nome para a lista.' : undefined;
  const erroOQue =
    tentou && !oQue.trim()
      ? 'Conte o que você precisa ver: é por aqui que a Cliqfy monta a consulta.'
      : undefined;

  const enviar = (e: FormEvent): void => {
    e.preventDefault();
    definirTentou(true);
    if (!nome.trim() || !oQue.trim() || pedir.isPending) return;
    pedir.mutate();
  };

  return (
    <Dialogo
      titulo="Pedir uma lista nova"
      forma="lateral"
      aoFechar={() => {
        if (!pedir.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={pedir.isPending}
            motivo="Enviando…"
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="pedir-lista"
            variante="primario"
            desabilitado={pedir.isPending || api === null}
            motivo={pedir.isPending ? 'Enviando…' : 'Sem sessão: entre de novo.'}
          >
            {pedir.isPending ? 'Enviando…' : 'Enviar pedido'}
          </Botao>
        </>
      }
    >
      <form id="pedir-lista" className="listas__form" onSubmit={enviar} noValidate>
        <p className="listas__nota">
          O suporte Cliqfy monta a lista no sistema da empresa, e ela aparece aqui, pronta.
        </p>
        <p className="listas__cuidado">
          Descreva a lista sem nome, telefone, documento ou placa de cliente: o pedido vai para a
          equipe da Cliqfy.
        </p>
        <Campo
          rotulo="Nome da lista"
          placeholder="Ex.: Cirurgias da semana"
          maxLength={120}
          value={nome}
          onChange={(e) => definirNome(e.target.value)}
          {...(erroNome ? { erro: erroNome } : {})}
        />
        <CampoArea
          rotulo="O que você precisa ver"
          placeholder="Ex.: quem opera nos próximos 7 dias, para a equipe ligar na véspera"
          rows={3}
          maxLength={2000}
          value={oQue}
          onChange={(e) => definirOQue(e.target.value)}
          {...(erroOQue ? { erro: erroOQue } : {})}
        />
        <Campo
          rotulo="Colunas que quer ver (opcional)"
          placeholder="Ex.: paciente, procedimento, data, profissional"
          maxLength={1000}
          value={colunas}
          onChange={(e) => definirColunas(e.target.value)}
        />
        {pedir.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(pedir.error, {
              generica: 'Não foi possível enviar agora. Tente de novo.',
              porCodigo: FORA,
            })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}

/**
 * Montar uma lista (Cliqfy): a partir de uma operação de listagem publicada,
 * as colunas que aparecem e o nome de cada uma. Ela nasce em montagem.
 */
function MontarLista({
  lista,
  finalidades,
  aoFechar,
  aoMontada,
  aoIrParaOperacoes,
}: {
  /** Com a lista, edita a definição dela; sem, monta uma nova. */
  lista?: Lista;
  /** As finalidades das OUTRAS listas, para avisar a repetida. */
  finalidades: readonly string[];
  aoFechar: () => void;
  aoMontada: (id: string, nome: string) => void;
  aoIrParaOperacoes?: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const operacoes = useOperacoesDeDados('listagem', true);
  const [chave] = useState(() => crypto.randomUUID());
  const [nome, definirNome] = useState(lista?.nome ?? '');
  const [descricao, definirDescricao] = useState(lista?.descricao ?? '');
  const [operacaoId, definirOperacaoId] = useState(lista?.operacao_id ?? '');
  const [titulos, definirTitulos] = useState<Record<string, string>>(() =>
    Object.fromEntries((lista?.colunas ?? []).map((c) => [c.nome, c.titulo])),
  );
  const [mostrar, definirMostrar] = useState<Record<string, boolean>>({});
  const [colunaChave, definirColunaChave] = useState(lista?.coluna_chave ?? '');
  const [colunaRotulo, definirColunaRotulo] = useState(lista?.coluna_rotulo ?? '');
  // A finalidade sai do nome até a pessoa escrever a sua; na edição, é a da lista.
  const [finalidadeEscrita, definirFinalidadeEscrita] = useState<string | null>(
    lista?.finalidade ?? null,
  );
  // Editando, a coluna que a lista não mostrava começa desmarcada.
  const mostra = (coluna: string): boolean =>
    mostrar[coluna] ?? (lista ? lista.colunas.some((c) => c.nome === coluna) : true);
  const [tentou, definirTentou] = useState(false);
  const finalidade = finalidadeEscrita ?? finalidadeDoNome(nome);

  const usaveis = (operacoes.data?.itens ?? []).filter(
    (o) => o.situacao === 'publicada' && o.tipo === 'leitura',
  );
  const operacao = usaveis.find((o) => o.id === operacaoId);
  const daOperacao = operacao?.colunas ?? [];
  const escolhidas = daOperacao.filter((c) => mostra(c.nome));

  const problemas: string[] = [];
  if (!nome.trim()) problemas.push('Dê um nome para a lista.');
  if (!FINALIDADE_VALIDA.test(finalidade)) {
    problemas.push('A finalidade usa só letras minúsculas, números e _, de 2 a 40.');
  }
  if (!operacao) problemas.push('Escolha a operação de onde a lista lê.');
  else if (daOperacao.length === 0) problemas.push('A operação não declarou colunas de saída.');
  else if (escolhidas.length === 0) problemas.push('Mostre pelo menos uma coluna.');
  if (operacao && escolhidas.length > 0) {
    // Desmarcar a coluna escolhida a tira das opções: ela não vale mais.
    if (!escolhidas.some((c) => c.nome === colunaChave)) {
      problemas.push('Escolha a coluna que identifica cada linha.');
    }
    if (!escolhidas.some((c) => c.nome === colunaRotulo)) {
      problemas.push('Escolha a coluna que dá o nome de cada linha.');
    }
  }

  const montar = useMutation({
    mutationFn: () => {
      const corpo = {
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        finalidade,
        operacao_id: operacaoId,
        coluna_chave: colunaChave,
        coluna_rotulo: colunaRotulo,
        colunas: escolhidas.map((c) => ({
          nome: c.nome,
          titulo: (titulos[c.nome] ?? '').trim() || c.nome,
        })),
        // A tela não monta o filtro pelo cliente: na edição, o da lista fica.
        filtro_por_cliente: lista?.filtro_por_cliente ?? null,
      };
      // Editar manda a versão lida: se outra pessoa salvou no meio, é 409.
      return lista
        ? api!.editarLista({ caminho: { id: lista.id }, corpo: { ...corpo, versao: lista.versao } })
        : api!.criarLista({ corpo, idempotencyKey: chave });
    },
    onSuccess: (salva) => {
      void cache.invalidateQueries({ queryKey: chaves.listas() });
      aoMontada(salva.id, salva.nome);
    },
  });

  const enviar = (e: FormEvent): void => {
    e.preventDefault();
    definirTentou(true);
    if (problemas.length > 0 || montar.isPending) return;
    montar.mutate();
  };

  return (
    <Dialogo
      titulo={lista ? `Editar ${lista.nome}` : 'Nova listagem'}
      forma="lateral"
      aoFechar={() => {
        if (!montar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={montar.isPending}
            motivo="Montando…"
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="montar-lista"
            variante="primario"
            desabilitado={montar.isPending || api === null}
            motivo={
              montar.isPending ? (lista ? 'Salvando…' : 'Montando…') : 'Sem sessão: entre de novo.'
            }
          >
            {montar.isPending ? (lista ? 'Salvando…' : 'Montando…') : lista ? 'Salvar' : 'Montar'}
          </Botao>
        </>
      }
    >
      <form id="montar-lista" className="listas__form" onSubmit={enviar} noValidate>
        <p className="listas__nota">
          A lista lê de uma operação de listagem já publicada. Só as colunas marcadas aparecem para
          a empresa;{' '}
          {lista
            ? 'salvar troca a definição, e a publicada continua publicada.'
            : 'ela nasce em montagem e vai ao ar quando você publicar.'}
        </p>
        <Campo
          rotulo="Nome da lista"
          placeholder="Ex.: Consultas de amanhã"
          maxLength={120}
          value={nome}
          onChange={(e) => definirNome(e.target.value)}
        />
        <Campo
          rotulo="Finalidade"
          ajuda={
            finalidades.includes(finalidade)
              ? 'Já há outra lista com esta finalidade.'
              : 'Um nome curto que agrupa as listas do mesmo assunto. Sai do nome; troque se precisar.'
          }
          maxLength={40}
          autoComplete="off"
          spellCheck={false}
          list="finalidades-das-listas"
          value={finalidade}
          onChange={(e) => definirFinalidadeEscrita(e.target.value)}
        />
        <datalist id="finalidades-das-listas">
          {finalidades.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
        <CampoArea
          rotulo="O que ela mostra (opcional)"
          ajuda="A empresa lê este texto em Listas."
          rows={2}
          maxLength={500}
          value={descricao}
          onChange={(e) => definirDescricao(e.target.value)}
        />
        <CampoSelecao
          rotulo="De qual operação"
          value={operacaoId}
          disabled={operacoes.isPending}
          onChange={(e) => {
            definirOperacaoId(e.target.value);
            definirColunaChave('');
            definirColunaRotulo('');
          }}
          ajuda={
            !operacoes.isPending && usaveis.length === 0
              ? 'Nenhuma operação de listagem publicada ainda.'
              : 'Só as operações de listagem publicadas, de leitura.'
          }
        >
          <option value="">{operacoes.isPending ? 'Carregando…' : 'Escolha a operação'}</option>
          {usaveis.map((o) => (
            <option key={o.id} value={o.id}>
              {o.nome}
            </option>
          ))}
        </CampoSelecao>
        {/* A lista não lê da conexão direto: lê de uma operação de listagem, e é ela
            que guarda o SQL. Sem nenhuma publicada, o caminho é escrever uma. */}
        {!operacoes.isPending && usaveis.length === 0 && aoIrParaOperacoes && (
          <div className="listas__sem-operacao">
            <p className="listas__nota">
              A lista não lê da conexão direto: ela lê de uma operação SQL de listagem, com as
              colunas de saída declaradas. Escreva uma em Operações SQL e volte aqui.
            </p>
            <Botao variante="secundario" miudo onClick={aoIrParaOperacoes}>
              Escrever uma operação de listagem
            </Botao>
          </div>
        )}

        {operacao && daOperacao.length > 0 && (
          <fieldset className="listas__colunas">
            <legend>Quais colunas aparecem, e com que nome</legend>
            {daOperacao.map((c) => (
              <div key={c.nome} className="listas__coluna">
                <label className="listas__marcar">
                  <input
                    type="checkbox"
                    checked={mostra(c.nome)}
                    onChange={(e) => {
                      definirMostrar({ ...mostrar, [c.nome]: e.target.checked });
                      if (!e.target.checked && colunaChave === c.nome) definirColunaChave('');
                      if (!e.target.checked && colunaRotulo === c.nome) definirColunaRotulo('');
                    }}
                  />
                  <code>{c.nome}</code>
                </label>
                <Campo
                  rotuloOculto={`Nome de ${c.nome} na tela`}
                  tamanho="miudo"
                  placeholder={c.nome}
                  maxLength={120}
                  value={titulos[c.nome] ?? ''}
                  disabled={!mostra(c.nome)}
                  onChange={(e) => definirTitulos({ ...titulos, [c.nome]: e.target.value })}
                />
              </div>
            ))}
          </fieldset>
        )}

        {operacao && escolhidas.length > 0 && (
          <>
            <CampoSelecao
              rotulo="A coluna que identifica cada linha"
              ajuda="Um valor que não se repete, como o código do agendamento."
              value={colunaChave}
              onChange={(e) => definirColunaChave(e.target.value)}
            >
              <option value="">Escolha</option>
              {escolhidas.map((c) => (
                <option key={c.nome} value={c.nome}>
                  {c.nome}
                </option>
              ))}
            </CampoSelecao>
            <CampoSelecao
              rotulo="A coluna que dá o nome de cada linha"
              ajuda="O que aparece quando a lista é usada como escolha, como o nome do paciente."
              value={colunaRotulo}
              onChange={(e) => definirColunaRotulo(e.target.value)}
            >
              <option value="">Escolha</option>
              {escolhidas.map((c) => (
                <option key={c.nome} value={c.nome}>
                  {c.nome}
                </option>
              ))}
            </CampoSelecao>
          </>
        )}

        {tentou && problemas.length > 0 && <Faixa tom="bloqueado">{problemas.join(' ')}</Faixa>}
        {montar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(montar.error, {
              generica: lista
                ? 'Não foi possível salvar agora. Nada mudou; tente de novo.'
                : 'Não foi possível montar agora. Tente de novo.',
              porCodigo: {
                ...FORA,
                CONFLITO_DE_VERSAO:
                  'Alguém mudou esta lista depois que você abriu. Feche e abra de novo para ver a versão nova.',
              },
            })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}

/** Responder um pedido (Cliqfy): atender com uma lista publicada, ou recusar com o motivo. */
function ResponderPedido({
  pedido,
  publicadas,
  aoFechar,
  aoRespondido,
}: {
  pedido: Pedido;
  publicadas: readonly Lista[];
  aoFechar: () => void;
  aoRespondido: (texto: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [listaId, definirListaId] = useState('');
  const [motivo, definirMotivo] = useState('');
  const invalidar = (): void => void cache.invalidateQueries({ queryKey: chaves.listas() });

  const atender = useMutation({
    mutationFn: () =>
      api!.atenderPedidoDeLista({ caminho: { id: pedido.id }, corpo: { lista_id: listaId } }),
    onSuccess: () => aoRespondido(`Pedido ${pedido.nome} atendido.`),
    onSettled: invalidar,
  });
  const recusar = useMutation({
    mutationFn: () =>
      api!.recusarPedidoDeLista({ caminho: { id: pedido.id }, corpo: { motivo: motivo.trim() } }),
    onSuccess: () => aoRespondido(`Pedido ${pedido.nome} recusado. O motivo fica com quem pediu.`),
    onSettled: invalidar,
  });
  const ocupado = atender.isPending || recusar.isPending;
  const falha = atender.error ?? recusar.error;

  return (
    <Dialogo
      titulo={`Responder: ${pedido.nome}`}
      forma="lateral"
      aoFechar={() => {
        if (!ocupado) aoFechar();
      }}
      botoes={
        <Botao variante="fantasma" onClick={aoFechar} desabilitado={ocupado} motivo="Enviando…">
          Fechar
        </Botao>
      }
    >
      <div className="listas__form">
        <blockquote className="listas__citacao">{pedido.o_que}</blockquote>
        {pedido.colunas_sugeridas && (
          <p className="listas__nota">Colunas pedidas: {pedido.colunas_sugeridas}</p>
        )}

        <fieldset className="listas__colunas">
          <legend>Atender com uma lista publicada</legend>
          <CampoSelecao
            rotuloOculto="A lista que atende o pedido"
            value={listaId}
            onChange={(e) => definirListaId(e.target.value)}
          >
            <option value="">Escolha a lista</option>
            {publicadas.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nome}
              </option>
            ))}
          </CampoSelecao>
          <Botao
            variante="primario"
            miudo
            onClick={() => atender.mutate()}
            desabilitado={!listaId || ocupado}
            motivo={!listaId ? 'Escolha a lista.' : 'Enviando…'}
          >
            Atender
          </Botao>
        </fieldset>

        <fieldset className="listas__colunas">
          <legend>Ou recusar</legend>
          <CampoArea
            rotulo="Motivo"
            placeholder="Ex.: o sistema da clínica não guarda o profissional da cirurgia"
            rows={2}
            maxLength={500}
            value={motivo}
            onChange={(e) => definirMotivo(e.target.value)}
          />
          <Botao
            variante="perigo"
            miudo
            onClick={() => recusar.mutate()}
            desabilitado={!motivo.trim() || ocupado}
            motivo={!motivo.trim() ? 'Escreva o motivo: é ele que quem pediu lê.' : 'Enviando…'}
          >
            Recusar
          </Botao>
        </fieldset>

        {falha && (
          <Faixa tom="bloqueado">
            {recadoDeErro(falha, {
              generica: 'Não foi possível responder agora. Tente de novo.',
              porCodigo: FORA,
            })}
          </Faixa>
        )}
      </div>
    </Dialogo>
  );
}

/** O que o menu sabe da última leitura de uma lista, nesta visita. */
type LidaNoMenu = { total: number; cortada: boolean } | 'falhou';

/** No item do menu: as linhas da última leitura, ou o alerta de que ela falhou. */
function LidaDoItem({ lida }: { lida: LidaNoMenu | undefined }): ReactNode {
  if (!lida) return null;
  if (lida === 'falhou') {
    return (
      <span className="listas__item-falhou">
        <Icone nome="alerta" />
        <span className="visualmente-oculto">A última leitura falhou</span>
      </span>
    );
  }
  const n = `${lida.total}${lida.cortada ? '+' : ''}`;
  return (
    // A chave pelo número refaz o "pulo" a cada leitura que muda o total.
    <span key={n} className="listas__item-contagem">
      {n}
      <span className="visualmente-oculto">{lida.total === 1 ? ' linha' : ' linhas'}</span>
    </span>
  );
}

const ICONE_DO_PEDIDO: Record<Pedido['situacao'] | 'sem-lista', NomeDoIcone> = {
  aguardando: 'atendimento',
  atendido: 'check',
  recusado: 'fechar',
  'sem-lista': 'caixa',
};
